import type { SyncProvider, SyncItem, SyncResult, SyncStatus, SyncedItem } from "../types.js";

const NOTION_API = "https://api.notion.com/v1";
const NOTION_VERSION = "2022-06-28";

const DEFAULT_STATUS_MAP: Record<string, SyncStatus> = {
  "Not started": "backlog",
  "To do": "todo",
  "In progress": "in_progress",
  "In review": "review",
  Done: "done",
  Cancelled: "cancelled",
  Canceled: "cancelled",
};

const REVERSE_STATUS_MAP: Record<SyncStatus, string> = {
  backlog: "Not started",
  todo: "To do",
  in_progress: "In progress",
  review: "In review",
  done: "Done",
  cancelled: "Cancelled",
};

async function notionRequest(
  apiKey: string,
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${NOTION_API}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
      "Notion-Version": NOTION_VERSION,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion API error (${res.status}): ${text}`);
  }

  return res.json() as Promise<Record<string, unknown>>;
}

function buildRichText(text: string): Array<{ type: string; text: { content: string } }> {
  // Notion rich text has a 2000 char limit per block
  const chunks: Array<{ type: string; text: { content: string } }> = [];
  for (let i = 0; i < text.length; i += 2000) {
    chunks.push({
      type: "text",
      text: { content: text.slice(i, i + 2000) },
    });
  }
  return chunks;
}

export function createNotionProvider(
  apiKey: string,
  databaseId: string,
  statusMapping?: Record<string, SyncStatus>,
): SyncProvider {
  const statusMap = { ...DEFAULT_STATUS_MAP, ...statusMapping };

  function mapToFathomStatus(notionStatus: string): SyncStatus {
    return statusMap[notionStatus] ?? "backlog";
  }

  return {
    name: "notion",

    async createItem(item: SyncItem): Promise<SyncResult> {
      const fathomMeta = `\nComplexity: ${item.complexity} | Priority: ${item.priority} | Est. Cost: $${item.estimatedCost.toFixed(2)}\nAcceptance Criteria: ${item.acceptanceCriteria}`;
      const fullDescription = `${item.description}\n\n---\nSynced from Fathom${fathomMeta}\n---`;

      const result = await notionRequest(apiKey, "POST", "/pages", {
        parent: { database_id: databaseId },
        properties: {
          Name: {
            title: buildRichText(item.title),
          },
          Status: {
            status: { name: "Not started" },
          },
          // Custom properties that the database may have
          ...(item.category && {
            Category: {
              select: { name: item.category },
            },
          }),
          ...(item.complexity && {
            Complexity: {
              select: { name: item.complexity },
            },
          }),
          ...(item.priority && {
            Priority: {
              select: { name: item.priority },
            },
          }),
        },
        children: [
          {
            object: "block",
            type: "paragraph",
            paragraph: {
              rich_text: buildRichText(fullDescription),
            },
          },
        ],
      });

      return {
        externalId: result.id as string,
        url: result.url as string,
        provider: "notion",
      };
    },

    async updateStatus(externalId: string, status: SyncStatus): Promise<void> {
      const notionStatus = REVERSE_STATUS_MAP[status] ?? "Not started";

      await notionRequest(apiKey, "PATCH", `/pages/${externalId}`, {
        properties: {
          Status: {
            status: { name: notionStatus },
          },
        },
      });
    },

    async getStatus(externalId: string): Promise<SyncStatus> {
      const page = await notionRequest(apiKey, "GET", `/pages/${externalId}`);
      const properties = page.properties as Record<
        string,
        { status?: { name: string } }
      >;

      const statusProp = properties.Status;
      if (!statusProp?.status?.name) return "backlog";

      return mapToFathomStatus(statusProp.status.name);
    },

    async listItems(): Promise<SyncedItem[]> {
      const result = await notionRequest(apiKey, "POST", `/databases/${databaseId}/query`, {
        filter: {
          property: "Name",
          rich_text: {
            contains: "", // Get all items; we filter by Fathom marker in content
          },
        },
        sorts: [{ property: "Name", direction: "ascending" }],
      });

      const pages = (result.results as Array<Record<string, unknown>>) ?? [];

      return pages
        .map((page) => {
          const properties = page.properties as Record<
            string,
            {
              title?: Array<{ plain_text: string }>;
              status?: { name: string };
            }
          >;

          const statusProp = properties.Status;
          const status = statusProp?.status?.name
            ? mapToFathomStatus(statusProp.status.name)
            : ("backlog" as SyncStatus);

          return {
            sliceId: (page.id as string) ?? "",
            externalId: page.id as string,
            url: page.url as string,
            status,
            lastSynced: new Date(page.last_edited_time as string).getTime(),
          };
        });
    },
  };
}
